import {BulkArchiveItemsResponse, BulkUnarchiveItemsResponse} from 'archive/ArchiveConstants';
import {DataModificationsHeader} from 'audit/DataModifications';
import {
  ParentStorableData,
  Storable,
  StorableSummary,
  StorableSummaryExtras,
} from 'constants/SampleConstants';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import {SearchParams, SearchResults} from 'search/SearchConstants';
import {postSearchRequest} from 'search/SearchDAO';
import * as net from 'util/Net';
import sanitizeFetchParams, {FetchParams} from 'util/sanitizeFetchParams';
import * as TaskRunner from 'util/TaskRunner';

export interface FetchStorablesResponse {
  storables: Array<StorableSummary>;
  limit: number;
  total: number;
  storableSummaryExtras: StorableSummaryExtras;
  error?: string;
}

export function fetchStorables(
  registryId: string | undefined,
  options: FetchParams
): Promise<FetchStorablesResponse> {
  const fetchParams = sanitizeFetchParams(options, 'filter', 'sort');
  return net.getJSONPromise('/1/api/storables', {
    registryId,
    ...fetchParams,
  });
}

export interface StorablesSearchResponse extends SearchResults {
  storableSummaries: Array<StorableSummary>;
  storableSummaryExtras: StorableSummaryExtras;
  error?: string;
}
const STORABLES_SEARCH_ENDPOINT = '/1/api/storables:search';
export function searchStorables(searchParams: SearchParams): Promise<StorablesSearchResponse> {
  return postSearchRequest({
    url: STORABLES_SEARCH_ENDPOINT,
    data: searchParams,
  });
}

export const STORABLES_INPUT_SEARCH_ENDPOINT = '/1/api/storables:input-search';
export interface StorablesInputSearchResponse {
  storables: Array<StorableSummary>;
  limit: number;
  total: number;
  storableSummaryExtras: StorableSummaryExtras;
}
export function inputSearchStorables(searchParams: SearchParams): Promise<StorablesInputSearchResponse> {
  return net.postJSONPromise({url: STORABLES_INPUT_SEARCH_ENDPOINT, data: searchParams});
}

export function bulkGetStorableSummaryExtras(storableIds: Array<string>): Promise<StorableSummaryExtras> {
  return net.postJSONPromise({
    url: '/1/api/storables:summary-extras',
    data: {storableIds},
  });
}

export function bulkGetStorablesWithAncestors(storableIds: Array<string>): Promise<Array<Storable>> {
  return net.postJSONPromise({
    url: '/1/api/storables:with-ancestors',
    data: {storableIds},
  });
}

export function bulkArchive(
  ids: Array<string>,
  purpose: string,
  {
    shouldRemoveBarcodes,
    shouldArchiveContainedSamples,
  }: {shouldRemoveBarcodes: boolean; shouldArchiveContainedSamples: boolean},
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<BulkArchiveItemsResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/storables:archive',
    data: {ids, purpose, shouldRemoveBarcodes, shouldArchiveContainedSamples},
    extraHeaders: dataModsHeader,
  });
}

export function bulkUnarchive(
  ids: Array<string>,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<BulkUnarchiveItemsResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/storables:unarchive',
    data: {ids},
    extraHeaders: dataModsHeader,
  });
}

export interface BulkFetchStorablesResponse {
  storables: Array<Storable>;
}

export function bulkFetchStorables(storableIds: Array<string>): Promise<BulkFetchStorablesResponse> {
  return net.postJSONPromise({
    url: '/1/api/storables:bulk-get',
    data: {storableIds},
  });
}

export interface BulkFetchStorableSummariesResponse {
  storableSummaries: Array<StorableSummary>;
}

export function bulkFetchSummaries(storableIds: Array<string>): Promise<BulkFetchStorableSummariesResponse> {
  return net.postJSONPromise({
    url: '/1/api/storables:bulk-get-summaries',
    data: {storableIds},
  });
}

export function fetchStarredStorables(
  registryId: string,
  options: FetchParams
): JQueryPromise<FetchStorablesResponse> {
  const fetchParams = sanitizeFetchParams(options, 'sort');
  return net.getJSON('/1/api/storables/starred', {registryId, ...fetchParams});
}

export function starStorables(storableIds: Array<string>): JQueryPromise<{}> {
  return net.postJSON({
    url: '/1/api/storables:star',
    data: {storableIds},
  });
}

export function unstarStorables(storableIds: Array<string>): JQueryPromise<{}> {
  return net.postJSON({
    url: '/1/api/storables:unstar',
    data: {storableIds},
  });
}

export interface BulkMoveStorableData {
  id: string;
  parentStorableData: ParentStorableData;
}

export interface BulkMoveResponse {
  invalidatableStorableIds: Array<string>;
}

export function bulkMove(
  storableDatas: Array<BulkMoveStorableData>,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<BulkMoveResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/storables:bulk-move',
    data: {storableDatas},
    extraHeaders: dataModsHeader,
  });
}

export interface BulkUpdateTagsResponse {
  storables: Array<Storable>;
}

export function bulkUpdateTags(
  storableIds: Array<string>,
  newTags: TagUpdateObject,
  dataModsHeader?: DataModificationsHeader
): Promise<BulkUpdateTagsResponse> {
  return net.postJSONPromise({
    url: '/1/api/storables:bulk-update-tags',
    data: {
      storableIds,
      tags: newTags,
    },
    extraHeaders: dataModsHeader,
  });
}
